import { Input, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import ContactUsModal from "@components/molecules/ContactUs";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";
import styled from "styled-components/native";
import OrderSummary from "./components/OrderSummary";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import ButtonContainer from "./components/ButtonContainer";
import { DefaultTheme } from "styled-components/native";
import { TouchableOpacity } from "react-native";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useValidateCoupon } from "@apis/billing";
import { AppliedCoupon } from "./utils";

const ManagePlanStep2 = ({
  lines,
  totals,
  t,
  typography,
  theme,
  trackButtonEvent,
  setShowContactUsModal,
  showContactUsModal,
  handleCreateContract,
  createContractMutation,
  disableReviewButton,
  handleReviewChangesPopup,
  isModifyPlan,
  productIds,
  appliedCoupon,
  setAppliedCoupon,
  hideCouponCode,
}: {
  lines: any;
  totals: any;
  t: any;
  typography: any;
  theme: any;
  trackButtonEvent: (
    event: string,
    component: string,
    data: any,
    view: string
  ) => void;
  setShowContactUsModal: (show: boolean) => void;
  showContactUsModal: boolean;
  handleCreateContract: (type: "confirm_plan" | "modify_plan") => void;
  createContractMutation: any;
  disableReviewButton: boolean;
  handleReviewChangesPopup: () => void;
  isModifyPlan: boolean;
  productIds: string[];
  appliedCoupon: AppliedCoupon;
  setAppliedCoupon: (coupon: AppliedCoupon) => void;
  hideCouponCode: boolean;
}) => {
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState<string | null>(null);
  const validateCouponMutation = useValidateCoupon();
  const { control, setValue } = useForm({
    resolver: yupResolver(
      yup.object().shape({
        couponCode: yup.string().required("Coupon code is required"),
      })
    ),
  });
  const applyCouponCode = (coupon: string) => {
    setCouponCode(coupon);
    if (couponError) setCouponError(null);
    if (appliedCoupon?.coupon_code) setAppliedCoupon(null);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError(null);
    setValue("couponCode", "");
  };
  const handleValidateCoupon = () => {
    if (!couponCode) return;
    validateCouponMutation.mutate(
      {
        coupon_code: couponCode,
        product_id: productIds?.filter(Boolean) ?? [],
      },
      {
        onSuccess: (data: any) => {
          const res = data?.data;
          if (res?.valid) {
            setCouponError(null);
            setAppliedCoupon({ ...res, coupon_code: couponCode });
          } else {
            setAppliedCoupon(null);
            setCouponError(
              t(
                "managePlan.popUp.coupon.invalid.text",
                "Invalid coupon! Please use a valid coupon code."
              )
            );
          }
        },
        onError: (error: any) => {
          setAppliedCoupon(null);
          setCouponError(
            error?.data?.message ??
              t(
                "managePlan.popUp.coupon.error.text",
                "Coupon validation failed"
              )
          );
        },
      }
    );
  };

  return (
    <Overlay>
      <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
        {t("managePlan.popUp.orderSummary.text", "Order Summary")}
      </VDBText>
      <Spacer size={SpaceType.Double} />
      <OrderSummary lines={lines} estimatedTotal={totals.estimatedTotal} />
      <Spacer size={SpaceType.Double} />
      <WarningContainer>
        <VDBIcon
          name={VDBIcons.WARNING}
          size={24}
          color={theme["warn-01"]}
          variant={IconsVariant.VDBCustom}
        />
        <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
          {t(
            "managePlan.popUp.orderSummary.screenText.text",
            "You'll have 90 days to apply this payment toward any VDB Solution."
          )}
        </VDBText>
      </WarningContainer>

      {!hideCouponCode && (
        <>
          <Spacer size={SpaceType.Double} />
          {!showCouponInput && !appliedCoupon?.coupon_code && !couponError ? (
            <CouponContainer>
              <VDBIcon
                name={VDBIcons.BRAND}
                size={24}
                color={theme["core-04"]}
                variant={IconsVariant.VDBCustom}
              />
              <TouchableOpacity onPress={() => setShowCouponInput(true)}>
                <VDBText
                  type={typography["Body-M"]}
                  color={theme["link-static"]}
                >
                  {t("managePlan.popUp.coupon.text", "Have a coupon code?")}
                </VDBText>
              </TouchableOpacity>
            </CouponContainer>
          ) : (
            <CouponWrapper>
              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    inputLabel={t(
                      "managePlan.popUp.coupon.input.label.text",
                      "Enter Your Coupon Code"
                    )}
                    placeholder={t(
                      "managePlan.popUp.coupon.input.placeholder.text",
                      "Enter Your Coupon Code"
                    )}
                    {...((appliedCoupon?.coupon_code || couponError) && {
                      leftIcon: VDBIcons.CLOSE_CIRCLE,
                      onLeftIconPress: handleRemoveCoupon,
                      leftIconColor: couponError ? "error-01" : "core-04",
                      disableLeftIcon: false,
                    })}
                    error={couponError ?? undefined}
                    hideErrorIcon={true}
                    onBlur={onBlur}
                    onChangeText={(val) => {
                      onChange(val);
                      applyCouponCode(val);
                    }}
                    value={value}
                    rightTextButton={true}
                    buttonText={t(
                      "managePlan.popUp.coupon.button.text",
                      "Apply"
                    )}
                    rightButtonDisabled={
                      !couponCode ||
                      validateCouponMutation.isPending ||
                      appliedCoupon?.coupon_code === couponCode
                    }
                    rightTextButtonOnPress={handleValidateCoupon}
                    onSubmitEditing={handleValidateCoupon}
                  />
                )}
                name="couponCode"
              />
              {appliedCoupon?.coupon_code ? (
                <>
                  <Spacer size={SpaceType.Basic} />
                  <CouponMessageRow>
                    <VDBText
                      type={typography["Minor-R"]}
                      color={theme["core-06"]}
                    >
                      {appliedCoupon.discount_type === "percent"
                        ? t(
                            "managePlan.popUp.coupon.success.text",
                            "Congratulations! You saved {{value}}% on your plan.",
                            { value: appliedCoupon.discount_value }
                          )
                        : t(
                            "managePlan.popUp.coupon.successApplied.text",
                            "Congratulations! Coupon applied to your plan."
                          )}
                    </VDBText>
                  </CouponMessageRow>
                </>
              ) : null}
            </CouponWrapper>
          )}
        </>
      )}
      <Spacer size={SpaceType.Big} />
      <ButtonContainer
        onConfirm={handleCreateContract}
        isLoading={createContractMutation.isPending}
        disabled={
          createContractMutation.isPending ||
          disableReviewButton ||
          !!couponError
        }
        handleReviewChangesPopup={handleReviewChangesPopup}
        isModifyPlan={isModifyPlan}
      />
      <Spacer size={SpaceType.Big} />
      <ContactUsContainer>
        <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
          {t("managePlan.popUp.contactUs.text", "Need help?")}{" "}
        </VDBText>
        <VDBText
          type={typography["Body-M"]}
          color={theme["link-static"]}
          onPress={() => {
            trackButtonEvent(
              EventName.BUTTON_CLICK,
              EventType.CONTACT_US_BUTTON_CLICK,
              { component_type: ViewType.POP_UP },
              ViewTypeEnum?.MANAGE_PLAN_POP_UP
            );
            setShowContactUsModal(true);
          }}
        >
          {t("contactUs.title.text")}
        </VDBText>

        <ContactUsModal
          isOpen={showContactUsModal}
          onClose={() => setShowContactUsModal(false)}
          screenName="Manage Plan"
        />
      </ContactUsContainer>
    </Overlay>
  );
};

const Overlay = styled.View`
  padding-vertical: ${SpaceType.Big}px;
  padding-horizontal: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? SpaceType.Basic : SpaceType.Big}px;
  align-items: center;
`;

const ContactUsContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const WarningContainer = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  border-radius: ${SpaceType.Basic}px;
  background-color: ${({ theme }: { theme: any }) => theme["warn-02"]};
  padding: ${SpaceType.Basic}px;
  gap: ${SpaceType.Basic}px;
`;

const CouponWrapper = styled.View`
  width: 100%;
`;

const CouponContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: ${SpaceType.Basic}px;
`;

const CouponMessageRow = styled.View`
  width: 100%;
`;

export default ManagePlanStep2;
